import { ContactType } from '@wonderschool/common-base-types';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Checkbox, CheckboxProps, Form } from 'semantic-ui-react';
import { FormComponentProps } from '../../../common';
import { ShowError } from '../../../Components/Messages';
import { useContacts } from '../../../contacts';
import { DEFAULT_PARENT } from '../../../students/consts';
import { isPickableFamilyContact } from '../../../students/studentsUtils';
import { ParentType, StudentAndParentsType } from '../../../students/types';
import EnrollmentParentEditForm from './EnrollmentParentEditForm';

enum NewOrExistingEnum {
  NEW = 'new',
  EXISTING = 'existing',
}

type EnrollmentParentFormProps = FormComponentProps<ParentType> & {
  parent: ParentType;
  parents?: ParentType[];
  form: UseFormReturn<StudentAndParentsType>;
  parentIndex: number;
};

function EnrollmentParentForm({ parent: parentParam, parents = [], form, parentIndex }: EnrollmentParentFormProps) {
  const { t } = useTranslation();
  const { contacts } = useContacts();

  const [selectedContact, setSelectedContact] = useState<ContactType | null>(parentParam?.contact ?? null);
  const [newOrExisting, setNewOrExisting] = useState<NewOrExistingEnum>(NewOrExistingEnum.NEW);

  useEffect(() => {
    if (parentParam?.contact) {
      setNewOrExisting(NewOrExistingEnum.EXISTING);
    }
  }, [parentParam?.contact]);

  const contactsNotInParents = useMemo(() => {
    return contacts.filter(
      (contact: any) =>
        isPickableFamilyContact(contact) &&
        !parents.find((parent) => parent.email === contact.email || parent.contact?.id === contact.id)
    );
  }, [contacts, parents]);

  const selectContactOptions = useMemo(() => {
    const options = contactsNotInParents.map((contact: any) => ({
      key: contact.id,
      text: contact.displayName,
      value: contact,
    }));
    return options;
  }, [contactsNotInParents]);

  const onChangeSelectedContact = useCallback(
    (e: any, { value }: { value?: any }) => {
      e.preventDefault();
      setSelectedContact(value);
      if (!value) return;

      const selectedParent = {
        ...parentParam,
        id: value.id,
        firstName: value.firstName,
        lastName: value.lastName,
        email: value.email,
        phone: value.phone,
        contact: value,
      } as ParentType;

      form.setValue(`parents.${parentIndex}`, selectedParent);
    },
    [parentParam, form, parentIndex]
  );

  const onChangeNewOrExisting = useCallback(
    (e: FormEvent<HTMLInputElement>, data: CheckboxProps) => {
      e.preventDefault();
      setSelectedContact(null);
      form.setValue(`parents.${parentIndex}`, { ...DEFAULT_PARENT });
      const { value } = data;
      if (value) setNewOrExisting(value as NewOrExistingEnum);
    },
    [form, parentIndex]
  );

  const displayError = () => {
    if (form.formState.errors.parents?.[parentIndex]) {
      const message = t('familyModal.selectContact');
      return <ShowError content={message} hideHeader={true} />;
    }
    return null;
  };

  return (
    <>
      <div className="parent-form">
        <Form.Group>
          <Form.Field>
            <Checkbox
              disabled={!!parentParam.isInterestedFamily}
              radio
              label={t('students.newParentLabel')}
              name="newOrExisting"
              value={NewOrExistingEnum.NEW}
              checked={newOrExisting === NewOrExistingEnum.NEW}
              onChange={onChangeNewOrExisting}
              data-testid="checkbox-newparent"
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              disabled={parentParam.isInterestedFamily}
              radio
              label={t('students.existingParentLabel')}
              name="newOrExisting"
              value={NewOrExistingEnum.EXISTING}
              checked={newOrExisting === NewOrExistingEnum.EXISTING}
              onChange={onChangeNewOrExisting}
              data-testid="checbox-existingparent"
            />
            {newOrExisting === NewOrExistingEnum.EXISTING && !!selectedContact && !parentParam.isInterestedFamily && (
              <>
                <span> (</span>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#" onClick={() => setSelectedContact(null)} data-testid="parentSelectDifferentParentLabel">
                  {t('students.parentSelectDifferentParentLabel')}
                </a>
                <span>) </span>
              </>
            )}
          </Form.Field>
        </Form.Group>

        {newOrExisting === NewOrExistingEnum.EXISTING && !selectedContact && !parentParam.isInterestedFamily ? (
          <Form.Field error={false}>
            <Form.Dropdown
              fluid
              search
              selection
              clearable
              required
              selectOnBlur={false}
              id="users"
              name="users"
              placeholder={t('common.selectContact')}
              options={selectContactOptions}
              value={selectedContact ? selectedContact['id'] : ''}
              onChange={onChangeSelectedContact}
              data-testid="select-contact"
            />
            {displayError()}
          </Form.Field>
        ) : (
          <EnrollmentParentEditForm
            hasTitle={false}
            form={form}
            parentIndex={parentIndex}
            readonly={newOrExisting === NewOrExistingEnum.EXISTING || parentParam.isInterestedFamily}
            parent={parentParam}
            parents={parents}
          />
        )}
      </div>
    </>
  );
}

export default EnrollmentParentForm;
